import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import styled, { css } from "styled-components";

interface CommentTextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  text?: string;
  fill?: boolean;
  className?: string;
  noBorder?: boolean;
}

const CommentTextArea = forwardRef<HTMLTextAreaElement, CommentTextAreaProps>(
  ({ disabled, fill, className, text, onChange, noBorder, ...args }, ref) => {
    const internalRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => internalRef.current!, []);

    const resizeTextarea = useCallback(() => {
      const textarea = internalRef.current;
      if (!textarea) return;

      // 스크롤 높이를 정확히 측정하기 위해 높이를 초기화
      textarea.style.height = "auto";

      // 현재 스크롤 높이를 가져와서 설정
      const newHeight = Math.max(textarea.scrollHeight, 24); // 최소 높이 24px
      textarea.style.height = `${newHeight}px`;
    }, []);

    const handleChange = useCallback(() => {
      resizeTextarea();
    }, [resizeTextarea]);

    // 초기 렌더링과 value 변경 시 높이 조절
    useLayoutEffect(() => {
      resizeTextarea();
    }, [resizeTextarea, args.value]);

    return (
      <CommentTextAreaLayout
        className={className}
        disabled={disabled ?? false}
        fill={fill}
        noBorder={noBorder}
      >
        <textarea
          {...args}
          {...(text !== "" ? { value: text } : {})}
          ref={internalRef}
          disabled={disabled}
          onChange={handleChange}
          rows={1}
        />
      </CommentTextAreaLayout>
    );
  },
);

CommentTextArea.displayName = "CommentTextArea";

export default CommentTextArea;

const CommentTextAreaLayout = styled.div<{
  disabled: boolean;
  fill?: boolean;
  noBorder?: boolean;
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  align-items: end;

  padding: 1.2rem 1.4rem;
  width: ${({ fill }) => (fill ? "100%" : "fit-content")};

  textarea {
    ${({ theme }) => theme.typography.body3.regular};
    border: none;
    outline: none;
    ${({ fill }) => fill && "width: 100%;"}
    resize: none;
    overflow: hidden;
    min-height: 24px;
    max-height: none;
    line-height: 1.5;
    box-sizing: border-box;
    word-wrap: break-word;
    white-space: pre-wrap;
    padding: 0;
    margin: 0;

    ::placeholder {
      color: ${({ theme }) => theme.color.text.placeholder};
    }

    :disabled {
      color: ${({ theme }) => theme.color.text.disabled};
    }
  }

  div {
    color: ${({ theme }) => theme.color.text.secondary};
  }

  ::after {
    content: "";
    display: block;
    position: absolute;
    box-sizing: border-box;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;

    pointer-events: none;
    border-radius: 0.5rem;
    border: ${({ theme, noBorder }) =>
      noBorder ? "none" : `1px solid ${theme.color.stroke.light}`};

    z-index: 100;
  }

  &:hover::after {
    border: ${({ theme, noBorder }) =>
      noBorder ? "none" : `1px solid ${theme.color.stroke.dark}`};
  }

  &:focus-within {
    ::after {
      border: ${({ theme, noBorder }) =>
        noBorder ? "none" : `1px solid ${theme.color.stroke.primary}`};
    }
    div > span:first-child {
      color: ${({ theme }) => theme.color.text.default};
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      ::after {
        background-color: ${({ theme }) => theme.color.background.dark};
        z-index: -1;
      }

      &:hover::after {
        border: 1px solid ${({ theme }) => theme.color.stroke.light};
      }

      div {
        color: ${({ theme }) => theme.color.text.disabled};
      }
    `}
`;
